'use es6';

import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useEffect, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import KnowledgeBaseLink from 'AdsUI/components/KnowledgeBaseLink';
import { ColumnValues, EntityTypes, TextColumns, KBArticles } from 'AdsUI/lib/Constants';
import { CANDY_APPLE } from 'HubStyleTokens/colors';
import UIButton from 'UIComponents/button/UIButton';
import UIButtonWrapper from 'UIComponents/layout/UIButtonWrapper';
import UIAlert from 'UIComponents/alert/UIAlert';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIAbstractProgress from 'UIComponents/progress/UIAbstractProgress';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UINanoProgress from 'UIComponents/progress/UINanoProgress';
import UITable from 'UIComponents/table/UITable';
import CampaignReportingDataCell from 'AdsUI/components/campaign-reporting/CampaignReportingDataCell';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import CampaignReportingNameCell from './CampaignReportingNameCell';
import EntityStatusToggleButton from '../EntityStatusToggleButton';
import UIPaginator from 'UIComponents/paginator/UIPaginator';
import THDragDrop from './THDragDrop';
import UIDragHandleHoverContainer from 'UIComponents/draggable/UIDragHandleHoverContainer';
import CampaignsListTableSearch from '../CampaignsListTableSearch';
import EditColumnsButton from '../campaigns/EditColumnsButton';
import TextSkeleton from '../TextSkeleton';
import { ROITooltipBody } from '../ROIValue';
import { usageTracker } from 'AdsUI/lib/usageTracker';
import AccountsTrackingErrorModal from 'AdsUI/components/tracking-error-modal/AccountsTrackingErrorModal';
import CampaignTrackingErrorModal from 'AdsUI/components/tracking-error-modal/CampaignTrackingErrorModal';
import AdGroupTrackingErrorModal from 'AdsUI/components/tracking-error-modal/AdGroupTrackingErrorModal';
import UIIllustration from 'UIComponents/image/UIIllustration';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import GoToCurrencySettings from '../GoToCurrencySettings';
import UITruncateString from 'UIComponents/text/UITruncateString';
import CampaignReportingWorkflowCell from 'AdsUI/components/campaign-reporting/CampaignReportingWorkflowCell';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';

const getAdAccount = (entity, adAccounts) => {
  if (entity) {
    return adAccounts.find(adAccount => adAccount.accountId === entity.accountId && adAccount.network === entity.network);
  }

  return null;
};

const getTooltip = (column, totals, attributionView, lifecycleStageLabelsByStageId) => {
  if (totals && column === ColumnValues.ROI) {
    return /*#__PURE__*/_jsx(ROITooltipBody, {
      data: totals
    });
  }

  const attributionTooltip = `campaigns.campaignTable.tooltips.${column}.${attributionView}`;

  if (column === ColumnValues.CONVERSIONS) {
    return /*#__PURE__*/_jsxs("span", {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: attributionTooltip
      }), ' ', /*#__PURE__*/_jsx(KnowledgeBaseLink, {
        kbArticle: KBArticles.AD_TRACKING,
        context: `${column}-campaign-reporting-column`
      })]
    });
  }

  if (isValidI18nKey(attributionTooltip)) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: attributionTooltip
    });
  }

  const columnTooltip = `campaigns.columnTooltips.${column}`;

  if (isValidI18nKey(columnTooltip)) {
    return /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: columnTooltip,
      options: Object.assign({
        goToCurrencySettings: /*#__PURE__*/_jsx(GoToCurrencySettings, {})
      }, lifecycleStageLabelsByStageId)
    });
  }

  return null;
};

const EmptyTable = ({
  colSpan,
  entityType,
  searchQuery
}) => /*#__PURE__*/_jsx("tbody", {
  children: /*#__PURE__*/_jsx("tr", {
    className: "empty-row text-center",
    children: /*#__PURE__*/_jsxs("td", {
      colSpan: colSpan,
      children: [/*#__PURE__*/_jsx(UIIllustration, {
        name: "empty-state-charts",
        width: 150
      }), /*#__PURE__*/_jsxs("div", {
        className: "m-top-3",
        children: [/*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx("strong", {
            children: searchQuery && searchQuery !== '' ? /*#__PURE__*/_jsx(FormattedMessage, {
              message: `campaigns.campaignTable.noCampaignsWithSearch`,
              options: {
                searchQuery
              }
            }) : /*#__PURE__*/_jsx(FormattedMessage, {
              message: `campaigns.campaignTable.noCampaigns.${entityType === 'ad-sequence' ? 'ad-sequence' : 'campaigns'}`
            })
          })
        }), entityType === EntityTypes.CAMPAIGN && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "campaigns.campaignTable.moreInfo_jsx",
            elements: {
              KnowledgeBaseLink
            },
            options: {
              kbArticle: KBArticles.AD_TRACKING,
              context: 'empty-campaigns-table'
            }
          })]
        })]
      })]
    })
  })
});

const CampaignReportingTableHeader = ({
  reportsLoading,
  columns,
  lifecycleStageLabelsByStageId,
  hasControlButtons,
  attributionView,
  totals,
  sortDirection,
  sortKey,
  onSortChange,
  onColumnReorder,
  canEditColumns,
  columnDisplayLabels,
  entityType,
  freeTier
}) => /*#__PURE__*/_jsx("thead", {
  children: /*#__PURE__*/_jsx(UIDragHandleHoverContainer, {
    children: /*#__PURE__*/_jsxs("tr", {
      children: [/*#__PURE__*/_jsx(THDragDrop, {
        editable: false,
        activeDirection: sortDirection,
        activeSortKey: sortKey,
        onSortChange: onSortChange,
        sortKey: ColumnValues.NAME,
        className: "campaign-name-cell",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "campaigns.campaignDetailsTable.name"
        })
      }, "name"), hasControlButtons && /*#__PURE__*/_jsx("th", {}), !freeTier && entityType === EntityTypes.AD && /*#__PURE__*/_jsx(THDragDrop, {
        editable: false,
        activeDirection: sortDirection,
        activeSortKey: sortKey,
        onSortChange: onSortChange,
        sortKey: ColumnValues.WORKFLOWS,
        className: "text-column",
        children: /*#__PURE__*/_jsx(UIHelpIcon, {
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "campaigns.campaignDetailsTable.workflowsTooltip"
          }),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "campaigns.campaignDetailsTable.workflows"
          })
        })
      }, "workflows"), columns.map(column => /*#__PURE__*/_jsx(THDragDrop, {
        sortDisabled: reportsLoading,
        editable: !reportsLoading && canEditColumns,
        title: getTooltip(column, totals, attributionView, lifecycleStageLabelsByStageId),
        tooltipProps: {
          use: column === ColumnValues.ROI ? 'longform' : 'default',
          maxWidth: column === ColumnValues.ROI ? 400 : undefined,
          onOpenChange: e => e.target.value && usageTracker.track('columnTooltipAction', {
            action: 'Opened column tooltip',
            column
          })
        },
        activeDirection: sortDirection,
        activeSortKey: sortKey,
        onSortChange: onSortChange,
        sortKey: column,
        className: `${TextColumns.indexOf(column) === -1 ? 'number-column' : 'text-column'} ${column.toLowerCase()}`,
        id: column,
        align: TextColumns.indexOf(column) === -1 ? 'right' : 'left',
        columns: columns,
        onColumnReorder: onColumnReorder,
        children: columnDisplayLabels[column]
      }, column))]
    })
  })
});

const CampaignReportingTableBody = ({
  columns,
  campaignChildren,
  entityType,
  parentEntity,
  adAccounts,
  campaignEntity,
  globalFilters,
  onClickEntity,
  hasControlButtons,
  toggleEntityStatus,
  hasWriteScope,
  hasRoiReportingScope,
  earlyFetch,
  errorsByAdAccount,
  hasCampaignManagement,
  columnDisplayLabels,
  sequenceStep,
  allWorkflows,
  freeTier
}) => {
  useEffect(() => {
    try {
      // Measure performance of table body loading
      if (!window.newrelic || !earlyFetch || !window.performance || !window.performance.now || !!window.HS_SKIP_CUSTOM_PERF) {
        return;
      }

      window.newrelic.addPageAction('ads-campaign-list-succeeded', {
        'ads-campaign-list-duration': window.performance.now()
      }); // eslint-disable-next-line
    } catch (e) {}
  }, [earlyFetch]);
  return /*#__PURE__*/_jsx("tbody", {
    children: campaignChildren.map(child => {
      const adAccount = getAdAccount(child.entity, adAccounts);
      return /*#__PURE__*/_jsxs("tr", {
        "data-test-id": `entity-row-${child.entity.id}`,
        children: [/*#__PURE__*/_jsx(CampaignReportingNameCell, {
          entity: child.entity,
          entityType: entityType,
          parent: parentEntity,
          adAccount: adAccount,
          campaignEntity: campaignEntity,
          timeRange: globalFilters.timeRange,
          trackingStatus: child.trackingStatus,
          onClickEntity: onClickEntity,
          errorsByAdAccount: errorsByAdAccount,
          hasCampaignManagement: hasCampaignManagement,
          hasControlButtons: hasControlButtons,
          sequenceStep: sequenceStep
        }), hasControlButtons && /*#__PURE__*/_jsx("td", {
          children: entityType !== EntityTypes.NEGATIVE_KEYWORD && child.entity.type !== EntityTypes.SEQUENCE && /*#__PURE__*/_jsx(EntityStatusToggleButton, {
            campaignEntity: campaignEntity || child.entity,
            entity: child.entity,
            adAccount: adAccount,
            entityType: entityType,
            hasRoiReportingScope: hasRoiReportingScope,
            hasWriteScope: hasWriteScope,
            errorsByAdAccount: errorsByAdAccount,
            onChange: () => toggleEntityStatus(child.entity),
            size: "extra-small"
          })
        }), !freeTier && entityType === EntityTypes.AD && /*#__PURE__*/_jsx("td", {
          children: allWorkflows && allWorkflows[child.entity.id] && allWorkflows[child.entity.id].length > 0 ? /*#__PURE__*/_jsx(UITruncateString, {
            children: /*#__PURE__*/_jsx(CampaignReportingWorkflowCell, {
              flows: allWorkflows ? allWorkflows[child.entity.id] : []
            })
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "campaigns.campaignDetailsTable.noWorkflowsText"
          })
        }), columns.map(column => child.report ? /*#__PURE__*/_jsx(CampaignReportingDataCell, {
          column: column,
          report: child.report,
          entity: child.entity,
          trackingStatus: child.trackingStatus,
          entityType: entityType,
          parent: parentEntity,
          campaignEntity: entityType === EntityTypes.CAMPAIGN ? child.entity : campaignEntity,
          adAccount: adAccount,
          globalFilters: globalFilters,
          columnDisplayLabels: columnDisplayLabels,
          hasControlButtons: hasControlButtons
        }, `${child.entity.id}${column}`) : /*#__PURE__*/_jsx("td", {
          className: TextColumns.indexOf(column) === -1 ? 'number-column' : '',
          children: earlyFetch && /*#__PURE__*/_jsx(TextSkeleton, {
            size: TextColumns.indexOf(column) === -1 ? 'default' : 'large'
          })
        }, `${child.entity.id}${column}`))]
      }, child.entity.id);
    })
  });
};

const CampaignReportingTableFooter = ({
  columns,
  totals,
  parent,
  parentTrackingStatus,
  entityType,
  adAccount,
  campaignEntity,
  globalFilters,
  columnDisplayLabels,
  hasControlButtons,
  freeTier,
  childEntityType
}) => /*#__PURE__*/_jsx("tfoot", {
  children: /*#__PURE__*/_jsxs("tr", {
    children: [/*#__PURE__*/_jsx("td", {
      colSpan: hasControlButtons ? 2 : 1,
      className: "campaign-reporting-data-cell",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "campaigns.totals"
      })
    }), !freeTier && childEntityType === EntityTypes.AD && /*#__PURE__*/_jsx("td", {}), totals ? columns.map(column => /*#__PURE__*/_jsx(CampaignReportingDataCell, {
      column: column,
      entityType: entityType,
      parent: campaignEntity,
      campaignEntity: campaignEntity,
      disableTrackingStatus: true,
      adAccount: adAccount,
      globalFilters: globalFilters,
      report: totals,
      entity: parent,
      trackingStatus: parentTrackingStatus,
      columnDisplayLabels: columnDisplayLabels
    }, `totals-${column}`)) : /*#__PURE__*/_jsx("td", {
      colSpan: columns.size
    })]
  })
});

const EntityTypeTrackingModalMap = {
  [EntityTypes.CAMPAIGN]: AccountsTrackingErrorModal,
  [EntityTypes.ADGROUP]: CampaignTrackingErrorModal,
  [EntityTypes.AD]: AdGroupTrackingErrorModal,
  [EntityTypes.KEYWORD]: AdGroupTrackingErrorModal
};

const SummaryTrackingAlert = ({
  accountId,
  adGroupEntity,
  allTrackingErrors,
  campaignEntity,
  entityType,
  globalFilters
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  if (!allTrackingErrors || !allTrackingErrors.size) {
    return /*#__PURE__*/_jsx("div", {});
  }

  const errorCount = allTrackingErrors.reduce((sum, {
    count
  }) => sum + count, 0);

  if (!errorCount) {
    return /*#__PURE__*/_jsx("div", {});
  }

  const EntityTrackingErrorModal = EntityTypeTrackingModalMap[entityType];
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(UIAlert, {
      type: "danger",
      use: "inline",
      className: "m-right-3",
      children: /*#__PURE__*/_jsx(UIButtonWrapper, {
        children: /*#__PURE__*/_jsxs(UIFlex, {
          wrap: "nowrap",
          align: "center",
          children: [/*#__PURE__*/_jsx(UIIcon, {
            name: "warning",
            color: CANDY_APPLE,
            className: "m-right-3"
          }), /*#__PURE__*/_jsx(UITruncateString, {
            matchContentWidth: true,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "campaigns.trackingStatus.summary",
              options: {
                count: errorCount
              }
            })
          }), /*#__PURE__*/_jsx(UIButton, {
            className: "m-left-3",
            use: "tertiary",
            size: "xs",
            onClick: () => setModalOpen(true),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "campaigns.trackingStatus.viewErrors"
            })
          })]
        })
      })
    }), modalOpen && /*#__PURE__*/_jsx(EntityTrackingErrorModal, {
      accountId: accountId,
      adGroupId: adGroupEntity && adGroupEntity.id,
      campaignId: campaignEntity && campaignEntity.id,
      name: adGroupEntity ? adGroupEntity.name : campaignEntity ? campaignEntity.name : undefined,
      network: campaignEntity && campaignEntity.network,
      globalFilters: globalFilters,
      allTrackingErrors: allTrackingErrors,
      onClose: () => setModalOpen(false)
    })]
  });
};

const CampaignReportingTable = /*#__PURE__*/memo(({
  header,
  navMarkerFooter,
  breadcrumb,
  loading,
  columns,
  lifecycleStageLabelsByStageId,
  sortDirection,
  sortKey,
  campaignChildren,
  entityType,
  parent,
  parentType,
  campaignEntity,
  adAccounts,
  globalFilters,
  hasWriteScope,
  hasRoiReportingScope,
  page,
  pageCount,
  searchQuery,
  errorsByAdAccount,
  allTrackingErrors,
  inEarlyRequest,
  totals,
  columnDisplayLabels,
  hasCampaignManagement,
  parentTrackingStatus,
  showEditColumns = true,
  showTotals = true,
  hasControlButtons = true,
  suppressHeader = false,
  suppressPagination = false,
  showFBStaleDataAlert = false,
  sequenceStep,
  onSortChange,
  onClickEntity,
  toggleEntityStatus,
  onPageChange,
  onColumnReorder,
  onSearchChange,
  onOpenEditColumnsModal,
  earlyFetch = false,
  allWorkflows,
  freeTier
}) => /*#__PURE__*/_jsxs(_Fragment, {
  children: [suppressHeader && showEditColumns && /*#__PURE__*/_jsx("div", {
    className: "justify-end m-bottom-2",
    children: /*#__PURE__*/_jsx(EditColumnsButton, {
      onClick: onOpenEditColumnsModal
    })
  }), !suppressHeader && /*#__PURE__*/_jsxs("div", {
    className: "justify-between m-bottom-2 align-center",
    children: [/*#__PURE__*/_jsxs("div", {
      className: "justify-between",
      children: [showFBStaleDataAlert && entityType === EntityTypes.CAMPAIGN && /*#__PURE__*/_jsx("div", {
        className: "m-right-3 align-end",
        children: /*#__PURE__*/_jsx(UITruncateString, {
          matchContentWidth: true,
          maxWidth: 400,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "campaigns.campaignTable.facebookStaleDataAlert"
          })
        })
      }), /*#__PURE__*/_jsx(SummaryTrackingAlert, {
        accountId: campaignEntity ? campaignEntity.accountId : undefined,
        adGroupEntity: entityType === EntityTypes.AD || entityType === EntityTypes.KEYWORD ? parent : undefined,
        allTrackingErrors: allTrackingErrors,
        campaignEntity: campaignEntity,
        entityType: entityType,
        globalFilters: globalFilters
      }), breadcrumb]
    }), /*#__PURE__*/_jsxs("div", {
      className: "justify-end",
      children: [showEditColumns && /*#__PURE__*/_jsx(EditColumnsButton, {
        onClick: onOpenEditColumnsModal
      }), /*#__PURE__*/_jsx(CampaignsListTableSearch, {
        use: "on-dark",
        campaignSearchQuery: searchQuery,
        onCampaignSearchChange: onSearchChange,
        entityType: entityType,
        network: campaignEntity && campaignEntity.network
      })]
    })]
  }), /*#__PURE__*/_jsxs(UICardWrapper, {
    compact: true,
    className: "m-bottom-0",
    children: [header, earlyFetch && /*#__PURE__*/_jsx(UIAbstractProgress, {
      autoStart: true,
      incrementFactor: 0.1,
      render: ({
        value
      }) => /*#__PURE__*/_jsx(UINanoProgress, {
        value: value,
        animateOnComplete: true
      }),
      value: campaignChildren && (campaignChildren.first() && campaignChildren.first().report || campaignChildren.size === 0) ? 100 : undefined
    }, globalFilters), /*#__PURE__*/_jsxs(UITable, {
      "data-test-id": "campaign-list-table",
      children: [/*#__PURE__*/_jsx(CampaignReportingTableHeader, {
        reportsLoading: earlyFetch && (!campaignChildren || campaignChildren.first() && !campaignChildren.first().report),
        totals: totals,
        sortDirection: sortDirection,
        sortKey: sortKey,
        onSortChange: onSortChange,
        columns: columns,
        lifecycleStageLabelsByStageId: lifecycleStageLabelsByStageId,
        canEditColumns: hasWriteScope && showEditColumns,
        onColumnReorder: onColumnReorder,
        attributionView: globalFilters.attributionView,
        columnDisplayLabels: columnDisplayLabels,
        hasControlButtons: hasControlButtons,
        entityType: entityType,
        freeTier: freeTier
      }), !loading && campaignChildren.size > 0 ? /*#__PURE__*/_jsx(CampaignReportingTableBody, {
        earlyFetch: earlyFetch,
        columnDisplayLabels: columnDisplayLabels,
        toggleEntityStatus: toggleEntityStatus,
        columns: columns,
        campaignChildren: campaignChildren,
        adAccounts: adAccounts,
        entityType: entityType,
        campaignEntity: campaignEntity,
        parentEntity: parent,
        globalFilters: globalFilters,
        onClickEntity: onClickEntity,
        hasWriteScope: hasWriteScope,
        hasRoiReportingScope: hasRoiReportingScope,
        errorsByAdAccount: errorsByAdAccount,
        hasCampaignManagement: hasCampaignManagement,
        hasControlButtons: hasControlButtons,
        sequenceStep: sequenceStep,
        allWorkflows: allWorkflows,
        freeTier: freeTier
      }) : !loading && !inEarlyRequest ? /*#__PURE__*/_jsx(EmptyTable, {
        colSpan: columns.size + 2,
        entityType: entityType,
        searchQuery: searchQuery
      }) : /*#__PURE__*/_jsx("tbody", {
        children: /*#__PURE__*/_jsx("tr", {
          children: /*#__PURE__*/_jsx("td", {
            colSpan: columns.size + 2,
            children: (!earlyFetch || campaignChildren && campaignChildren.size === 0) && /*#__PURE__*/_jsx(UILoadingSpinner, {
              layout: "centered"
            })
          })
        })
      }), showTotals && (loading || inEarlyRequest || campaignChildren.size > 0) && /*#__PURE__*/_jsx(CampaignReportingTableFooter, {
        columns: columns,
        totals: totals,
        parent: parent,
        columnDisplayLabels: columnDisplayLabels,
        parentTrackingStatus: parentTrackingStatus,
        campaignEntity: campaignEntity,
        entityType: parentType,
        adAccount: parent && getAdAccount(parent, adAccounts),
        globalFilters: globalFilters,
        hasControlButtons: hasControlButtons,
        freeTier: freeTier,
        childEntityType: entityType
      })]
    }), !suppressPagination && pageCount > 1 && /*#__PURE__*/_jsx("div", {
      className: "m-x-auto m-bottom-3",
      children: /*#__PURE__*/_jsx(UIPaginator, {
        onPageChange: onPageChange,
        page: page,
        pageCount: pageCount
      })
    }), navMarkerFooter]
  })]
}));
CampaignReportingTable.displayName = 'CampaignReportingTable';
export default CampaignReportingTable;